import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AppService } from '@Core/services/proprietary/app.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { TaskObjectState } from '../task/task-object.state';
import {
    ResetState as UserDefinedResetState,
    SetUserDefinedTableData
} from '../user-defined/user-defined-object.actions';
import { UserDefinedObjectState } from '../user-defined/user-defined-object.state';
import { AddressState } from './address.state';
import { DeleteArticle, UpdateArticle } from './article.actions';
import { ArticleState } from './article.state';
import { AttachmentState } from './attachment.state';
import { ConditionState } from './condition.state';
import {
    AddDefaultLocation,
    AddNewProfileForClonedArticle,
    DeleteArticlesLinkedToProfile,
    InitializeTransaction,
    LinkProfileToArticle,
    PrepareRequest,
    ResetState,
    SelfAuditCalculateReporting,
    SetContractObjectState,
    SyncBrokerPartyAffiliateFromContractBrokerAffiliate,
    SyncBrokerPartyFromContractBroker,
    SyncProcedureEstFullTermPrem
} from './contract-object.actions';
import { ContractObjectDefaults } from './contract-object.defaults';
import {
    ArticleNodeType,
    ArticleTypeByArticleNodeType,
    CostType,
    EntityNodeType,
    PartyNodeType,
    TransactionNodeType
} from './contract-object.types';
import { UpdateContractProcedure } from './contract-procedure.actions';
import { ContractProcedureState } from './contract-procedure.state';
import { SetBillToEntity } from './contract.actions';
import { ContractState } from './contract.state';
import { SetRetailBroker } from './entity.actions';
import { EntityState } from './entity.state';
import { FactorState } from './factor.state';
import { FormState } from './form.state';
import { InterestState } from './interest.state';
import { LineState } from './line.state';
import { AddLocation, UpdateLocation } from './location.actions';
import { LocationState } from './location.state';
import { NameState } from './name.state';
import { PartyAgreementService } from './party-agreement.service';
import { AddParty, ClearParty, DeleteParty, UpdateParty } from './party.actions';
import { PartyState } from './party.state';
import { PhoneState } from './phone.state';
import { PriorHistoryState } from './prior-history.state';
import { UpdateProductTotals } from './product.actions';
import { ProductState } from './product.state';
import { AddProfile } from './profile.actions';
import { ProfileState } from './profile.state';
import { RiskGroupState } from './risk-group.state';
import { SubLocationState } from './sub-location.state';
import { TeamMemberState } from './team-member.state';

export interface ContractObjectStateModel {
    entities: any;
    transactionType: TransactionNodeType;
    contractProcedureState: any;
    contractState: any;
    entityState: any;
    addressState: any;
    phoneState: any;
    riskGroupState: any;
    lineState: any;
    partyState: any;
    articleState: any;
    formState: any;
    factorState: any;
    productState: any;
    locationState: any;
    subLocationState: any;
    interestState: any;
    nameState: any;
    conditionState: any;
    profileState: any;
    contractAttachmentState: any;
    teamMemberState: any;
    priorHistoryState: any;
}

const ContractObjectStateDefaults: ContractObjectStateModel = {
    entities: {},
    transactionType: null,
    contractProcedureState: {},
    contractState: {},
    entityState: {},
    addressState: {},
    phoneState: {},
    riskGroupState: {},
    lineState: {},
    partyState: {},
    articleState: {},
    formState: {},
    factorState: {},
    productState: {},
    locationState: {},
    subLocationState: {},
    interestState: {},
    nameState: {},
    conditionState: {},
    profileState: {},
    contractAttachmentState: {},
    teamMemberState: {},
    priorHistoryState: {}
};

@State<ContractObjectStateModel>({
    name: 'contractObjectState',
    defaults: ContractObjectStateDefaults,
    children: [
        ContractProcedureState,
        ContractState,
        EntityState,
        AddressState,
        PhoneState,
        RiskGroupState,
        LineState,
        PartyState,
        ArticleState,
        FormState,
        FactorState,
        ProductState,
        LocationState,
        SubLocationState,
        InterestState,
        NameState,
        ConditionState,
        ProfileState,
        AttachmentState,
        TeamMemberState,
        PriorHistoryState
    ]
})
@Injectable()
export class ContractObjectState {
    constructor(
        private helper: StateHelperService,
        private app: AppService,
        private store: Store,
        private autoSelectAgmtServce: PartyAgreementService
    ) { }

    //************** SELECTORS  ***************

    @Selector()
    static transactionType(state: ContractObjectStateModel) {
        return state.transactionType;
    }

    @Selector()
    static articles(state: ContractObjectStateModel) {
        //return all articles
        let result: any = [];
        for (var i in state.entities.Article) {
            result.push(state.entities.Article[i]);
        }
        return result;
    }

    @Selector()
    static driverCodeSet(state: ContractObjectStateModel) {
        let driverList: any = [];
        let driver: any = {};
        let driverDesc: any;
        let articles = { ...state.articleState.article };
        let profiles = { ...state.profileState.profile };
        let profileID = '';

        for (var i in articles) {
            driver = {};
            driverDesc = '';
            profileID = '';
            if (articles[i].nodeType == ArticleNodeType.CDAA_Driver && articles[i].Edit_Status != 'D') {
                profileID = articles[i].Profile_ID;
                if (profileID != '') {
                    if (profiles[profileID].Indv_Last_Name && profiles[profileID].Indv_Last_Name != '') {
                        driverDesc = profiles[profileID].Indv_Last_Name + ',';
                    }
                    if (profiles[profileID].Indv_First_Name && profiles[profileID].Indv_First_Name != '') {
                        driverDesc += ' ' + profiles[profileID].Indv_First_Name;
                    }
                    if (profiles[profileID].Indv_Middle_Name && profiles[profileID].Indv_Middle_Name != '') {
                        driverDesc += ' ' + profiles[profileID].Indv_Middle_Name;
                    }
                }

                driver = { Code: articles[i].Article_ID, Code_Desc: driverDesc };
                driverList.push(driver);
            }
        }
        return driverList || [];
    }

    @Selector()
    static billToEntityCodeSet(state: ContractObjectStateModel) {
        let index = -1;
        let result: any = [];

        if (state.entityState.entity[state.entityState.currentConsumerID]) {
            let consumer = state.entityState.entity[state.entityState.currentConsumerID];
            result.push({ Code: consumer.Entity_ID, Code_Desc: consumer.Entity_Name, Role_Type: 'CN' });
        }

        let parties = state.partyState.party;
        for (var i in parties) {
            if (
                parties[i].nodeType == PartyNodeType.Contract_FinanceCo &&
                parties[i].FinCo.Entity_ID &&
                parties[i].FinCo.Entity_ID != '' &&
                parties[i].Edit_Status != 'D'
            ) {
                index = -1;
                index = result.map((x) => x.Code).indexOf(parties[i].FinCo.Entity_ID);
                if (index == -1) {
                    result.push({ Code: parties[i].FinCo.Entity_ID, Code_Desc: parties[i].FinCo.Entity_Name, Role_Type: 'FC' });
                }
            }

            if (
                parties[i].nodeType == PartyNodeType.Contract_Broker &&
                parties[i].Broker.Entity_ID &&
                parties[i].Broker.Entity_ID != '' &&
                parties[i].Edit_Status != 'D'
            ) {
                index = -1;
                index = result.map((x) => x.Code).indexOf(parties[i].Broker.Entity_ID);
                if (index == -1) {
                    result.push({ Code: parties[i].Broker.Entity_ID, Code_Desc: parties[i].Broker.Entity_Name, Role_Type: 'BK' });
                }
            }
        }

        let tpBillToEntity = state.partyState.tpBillToEntity;
        if (tpBillToEntity && tpBillToEntity.Entity_ID) {
            result.push({ Code: tpBillToEntity.Entity_ID, Code_Desc: tpBillToEntity.Entity_Name, Role_Type: 'TP' });
        }

        result.push({ Code: 'TPSearch', Code_Desc: 'Select a Third Party Entity ...' });

        return result;
    }

    @Selector()
    static articlesReferencingLocNum(state: ContractObjectStateModel) {
        return (locNum: string) => {
            let result: any = [];
            let locState = '';
            let lineCode = '';
            let articleDesc = '';

            for (var loc in state.locationState.location) {
                if (
                    state.locationState.location[loc].Loc_Num == locNum &&
                    state.locationState.location[loc].Edit_Status != 'D' &&
                    state.locationState.location[loc].Edit_Status != 'X'
                ) {
                    locState = state.locationState.location[loc].State;
                    break;
                }
            }

            for (var i in state.articleState.article) {
                articleDesc = '';
                lineCode = '';
                if (
                    state.articleState.article[i].Loc_Num == locNum &&
                    state.articleState.article[i].Edit_Status != 'D' &&
                    state.articleState.article[i].Edit_Status != 'X'
                ) {
                    articleDesc = state.articleState.article[i].Article_Desc;

                    if (
                        !articleDesc ||
                        articleDesc == null ||
                        articleDesc == undefined ||
                        state.articleState.article[i].nodeType === 'Line_Wide'
                    ) {
                        lineLoop: for (var l in state.lineState.line) {
                            if (state.lineState.line[l].Line_ID == state.articleState.article[i].Line_ID) {
                                lineCode = state.lineState.line[l].Line_Code;
                                if (state.articleState.article[i].nodeType === 'Line_Wide') {
                                    articleDesc = `Line(${lineCode}), State(${locState})`;
                                } else {
                                    // eslint-disable-next-line max-len
                                    articleDesc = `Line(${lineCode}), Article Type(${ArticleTypeByArticleNodeType[state.articleState.article[i].nodeType]
                                        })`;
                                }

                                break lineLoop;
                            }
                        }
                    }

                    result.push({
                        Article_ID: state.articleState.article[i].Article_ID ? state.articleState.article[i].Article_ID : '',
                        Article_Type: state.articleState.article[i].nodeType ? state.articleState.article[i].nodeType : '',
                        Article_Desc: articleDesc
                    });
                }
            }
            return result.length > 0 ? result.sort((a, b) => a.Article_Desc.localeCompare(b.Article_Desc)) : result;
        };
    }

    @Selector()
    static productTotalsByCostType(state: ContractObjectStateModel) {
        return (costType: CostType) => {
            let products: any = [];
            let product: any = {};
            let lineWideTotal: number = 0;
            let riskTotal: number = 0;

            for (var i in state.articleState.article) {
                products = state.articleState.article[i].Product;

                for (var p in products) {
                    product = { ...state.productState.product[products[p]] };
                    if (product.Product_Cost_Type == costType) {
                        if (state.articleState.article[i].nodeType == ArticleNodeType.Line_Wide) {
                            lineWideTotal += +product.Full_Term_Amount;
                        } else {
                            riskTotal += +product.Full_Term_Amount;
                        }
                    }
                }
            }

            return { Total: lineWideTotal + riskTotal, Line_Wide_Total: lineWideTotal, Risk_Total: riskTotal };
        };
    }

    @Selector()
    static productsByArticleAndCostType(state: ContractObjectStateModel) {
        return (articleType?: ArticleNodeType, costType?: CostType) => {
            let result: any = [];
            let products: any = [];
            let product: any = {};
            let limitDeduct = '';
            let limit1 = '';
            let limit2 = '';
            let limit3 = '';
            let limitDesc = '';
            let deduct = '';

            let location: any = {};
            let sublocation: any = {};
            let line: any = {};
            let articleDesc = '';

            for (var i in state.articleState.article) {
                if (!articleType || state.articleState.article[i].nodeType == articleType) {
                    articleDesc = state.articleState.article[i].Article_Desc;
                    if (
                        state.articleState.article[i].nodeType == ArticleNodeType.CDAA_Vehicle &&
                        state.articleState.article[i].VIN &&
                        state.articleState.article[i].VIN != ''
                    ) {
                        articleDesc += '[VIN: ' + state.articleState.article[i].VIN + ']';
                    }

                    for (var l in state.locationState.location) {
                        if (state.locationState.location[l].Loc_Num == state.articleState.article[i].Loc_Num) {
                            location = state.locationState.location[l];
                            break;
                        }
                    }
                    for (var l in state.subLocationState.subLocation) {
                        if (state.subLocationState.subLocation[l].Sub_Loc_Num == state.articleState.article[i].Sub_Loc_Num) {
                            sublocation = state.locationState.location[l];
                            break;
                        }
                    }

                    line = { ...state.lineState.line[state.articleState.article[i].Line_ID] };

                    products = state.articleState.article[i].Product;

                    for (var p in products) {
                        limitDeduct = '';
                        limit1 = '';
                        limit2 = '';
                        limit3 = '';
                        limitDesc = '';
                        deduct = '';

                        product = { ...state.productState.product[products[p]] };
                        if (!costType || product.Product_Cost_Type == costType) {
                            if (!product.Incl_Excl || product.Incl_Excl == '') {
                                product.Incl_Excl = '';
                                product.Incl_Excl_Desc = 'Amount';
                            }

                            if (product.Limit_1) {
                                limit1 = product.Limit_1;
                            }
                            if (product.Limit_2) {
                                limit2 = '/' + product.Limit_2;
                            }
                            if (product.Limit_3) {
                                limit3 = '/' + product.Limit_3;
                            }
                            if (product.Limit_Desc) {
                                limitDesc = 'Desc: ' + product.Limit_Desc;
                            }
                            if (product.Ded_1) {
                                deduct = 'Ded: ' + product.Ded_1;
                            }

                            limitDeduct = limit1 + limit2 + limit3;

                            if (limitDesc.length > 0) {
                                if (limitDeduct.length > 0) {
                                    limitDeduct += ' ';
                                }
                                limitDeduct += limitDesc;
                            }

                            if (deduct.length > 0) {
                                if (limitDeduct.length > 0) {
                                    limitDeduct += ' ';
                                }
                                limitDeduct += deduct;
                            }

                            if (limitDeduct.startsWith('/')) {
                                limitDeduct = limitDeduct.substring(1);
                            }

                            product.Limit_Deduct = limitDeduct;
                            product.Article_Status = product.Article_Status = state.articleState.article[i].Article_Status;
                            product.Article_Desc = articleDesc || '';
                            product.Article_Type =
                                state.articleState.article[i].nodeType == ArticleNodeType.Line_Wide ? 'Line' : 'Unit';
                            product.Article_Class_Code = state.articleState.article[i].Class_Code || '';
                            product.Article_Territory_Code = state.articleState.article[i].Territory_Code || '';
                            product.Loc_Num = location.Loc_Num || '';
                            product.State = location.State || '';
                            product.State_Desc = location.State_Desc || '';
                            product.Sub_Loc_Num = sublocation.Sub_Loc_Num || '';
                            product.Line_Code = line.Line_Code || '';
                            product.Line_Code_Desc = line.Line_Code_Desc || '';
                            product.Fully_Earned_Ind_Desc = product.Fully_Earned_Ind == 'Y' ? 'When Written' : 'Daily';

                            result.push(product);
                        }
                    }
                }
            }

            return result;
        };
    }

    @Selector()
    static productsWithoutCostByLine(state: ContractObjectStateModel) {
        return (lineCode: string) => {
            let result: any = [];
            let products: any = [];
            let product: any = {};
            let limitDeduct = '';
            let limit1 = '';
            let limit2 = '';
            let limit3 = '';
            let limitDesc = '';
            let deduct = '';
            let line: any = {};

            for (var l in state.lineState.line) {
                if (state.lineState.line[l].Line_Code.toLowerCase() == lineCode.toLowerCase()) {
                    line = state.lineState.line[l];
                    break;
                }
            }

            for (var i in state.articleState.article) {
                if (state.articleState.article[i].Line_ID == line.Line_ID) {
                    products = state.articleState.article[i].Product;

                    for (var p in products) {
                        limitDeduct = '';
                        limit1 = '';
                        limit2 = '';
                        limit3 = '';
                        limitDesc = '';
                        deduct = '';

                        product = cloneDeep(state.productState.product[products[p]]);

                        if (product.Limit_1) {
                            limit1 = product.Limit_1;
                        }
                        if (product.Limit_2) {
                            limit2 = '/' + product.Limit_2;
                        }
                        if (product.Limit_3) {
                            limit3 = '/' + product.Limit_3;
                        }
                        if (product.Limit_Desc) {
                            limitDesc = 'Desc: ' + product.Limit_Desc;
                        }
                        if (product.Ded_1) {
                            deduct = 'Ded: ' + product.Ded_1;
                        }

                        limitDeduct = limit1 + limit2 + limit3;

                        if (limitDesc.length > 0) {
                            if (limitDeduct.length > 0) {
                                limitDeduct += ' ';
                            }
                            limitDeduct += limitDesc;
                        }

                        if (deduct.length > 0) {
                            if (limitDeduct.length > 0) {
                                limitDeduct += ' ';
                            }
                            limitDeduct += deduct;
                        }

                        if (limitDeduct.startsWith('/')) {
                            limitDeduct = limitDeduct.substring(1);
                        }

                        //Remove Cost related data
                        delete product.Net_Change_Method;
                        delete product.Net_Change_Method_Desc;
                        delete product.Full_Term_Amount;
                        delete product.Net_Change_Amount;
                        delete product.Written_Amount;
                        delete product.Prev_Full_Term_Amount;
                        delete product.Prev_Written_Amount;
                        delete product.Fully_Earned_Ind;
                        delete product.Reporting_Fully_Earned_Ind;
                        delete product.Rate_1;
                        delete product.Rate_2;
                        delete product.Rate_3;
                        delete product.Rate_4;
                        delete product.Transaction_Rule_ID;
                        delete product.Cost_Status;
                        delete product.Article_ID;
                        delete product.Edit_Status;
                        delete product.Cost_ID;
                        delete product.Cost_GUID;

                        product.Limit_Deduct = limitDeduct;
                        result.push(product);
                    }
                }
            }

            return result;
        };
    }

    @Selector()
    static productSetCriteria(state: ContractObjectStateModel) {
        return (articleID: any) => {
            let result: any = {};

            const contract = state.contractState.contract[state.contractState.currentID];
            const procedureType = state.contractProcedureState.procedureType;

            let article: any = {};
            let line: any = {};
            let articleType = '';
            let lineCode = '';
            let program = '';
            let controllingState = contract.Controling_State;
            let sourceEntity = '';
            let riskEntity = '';
            let effDate = contract.Eff_Date;
            let firstRevision = '';

            if (contract.Contract_ID != contract.Contract_Set_ID) {
                firstRevision = 'N';
            } else {
                switch (procedureType) {
                    case TransactionNodeType.Audit:
                    case TransactionNodeType.Cancel:
                    case TransactionNodeType.ChangeConsumer:
                    case TransactionNodeType.Endorsement:
                    case TransactionNodeType.ExtendTerm:
                    case TransactionNodeType.Reissue:
                    case TransactionNodeType.Renewal:
                    case TransactionNodeType.Rewrite:
                    case TransactionNodeType.SelfAudit:
                        firstRevision = 'N';
                        break;
                    default:
                        firstRevision = 'Y';
                        break;
                }
            }

            if (state.partyState && state.partyState.party) {
                for (var p in state.partyState.party) {
                    if (
                        state.partyState.party[p].nodeType == PartyNodeType.Contract_Supplier &&
                        state.partyState.party[p].Principal_Ind == 'Y'
                    ) {
                        if (
                            state.partyState.party[p].Source_Supplier.Entity_ID &&
                            state.partyState.party[p].Source_Supplier.Entity_ID != ''
                        ) {
                            sourceEntity = state.partyState.party[p].Source_Supplier.Entity_ID;
                        }
                        if (
                            state.partyState.party[p].Risk_Supplier.Entity_ID &&
                            state.partyState.party[p].Risk_Supplier.Entity_ID != ''
                        ) {
                            riskEntity = state.partyState.party[p].Risk_Supplier.Entity_ID;
                        }
                        break;
                    }
                }
            }

            if (articleID && articleID != '') {
                if (state.articleState && state.articleState.article && state.articleState.article[articleID]) {
                    article = state.articleState.article[articleID];
                }

                if (article && article.Article_ID) {
                    //Continue.

                    articleType = ArticleTypeByArticleNodeType[article.nodeType];

                    let lineID = article.Line_ID;

                    if (lineID && lineID != '') {
                        if (state.lineState && state.lineState.line && state.lineState.line[lineID]) {
                            line = state.lineState.line[lineID];

                            lineCode = line.Line_Code;
                            program = line.Program || '';
                        }
                    }
                }
            }

            if (contract && line && article) {
                result = {
                    Line_Code: lineCode,
                    Sub_Line_Code: program,
                    State: controllingState,
                    Source_Entity_ID: sourceEntity,
                    Risk_Entity_ID: riskEntity,
                    Eff_Date: effDate,
                    Auto_Display_Ind: 'N',
                    Initial_Rev_Ind: firstRevision,
                    Article_Type: articleType
                };
            }

            return result;
        };
    }

    ///////////////////////////////////

    @Selector()
    static fullState(state: ContractObjectStateModel) {
        //temporary selector for debugging -- subscribing to this live could be expensive
        return state;
    }

    @Selector()
    static entities(state: ContractObjectStateModel) {
        let tempEntities = { ...state.entities };
        tempEntities.Procedure = state.contractProcedureState.procedure;
        tempEntities.Contract = state.contractState.contract;
        tempEntities.Entity = state.entityState.entity;
        tempEntities.Address = state.addressState.address;
        tempEntities.Phone = state.phoneState.phone;
        tempEntities.RiskGroup = state.riskGroupState.riskGroup;
        tempEntities.Line = state.lineState.line;
        tempEntities.Party = state.partyState.party;
        tempEntities.Article = state.articleState.article;
        tempEntities.SubArticle = state.articleState.subArticle;
        tempEntities.Form = state.formState.form;
        tempEntities.Factor = state.factorState.factor;
        tempEntities.Product = state.productState.product;
        tempEntities.Location = state.locationState.location;
        tempEntities.SubLocation = state.subLocationState.subLocation;
        tempEntities.Interest = state.interestState.interest;
        tempEntities.Name = state.nameState.name;
        tempEntities.Condition = state.conditionState.condition;
        tempEntities.Profile = state.profileState.profile;
        tempEntities.Attachment = state.contractAttachmentState.attachment;
        tempEntities.TeamMember = state.teamMemberState.teamMember;
        tempEntities.PriorHistory = state.priorHistoryState.priorHistory;
        tempEntities.LossHistory = state.priorHistoryState.lossHistory;
        return tempEntities;
    }

    //******  ACTIONS  ******

    @Action(PrepareRequest)
    prepareRequest(sc: StateContext<ContractObjectStateModel>, action: PrepareRequest) {
        // prepare ConceptOneRequest
        // Note: The normalize process will create a Root, Reply, and Transaction entity with an ID of 0
        //       When preparing a process transaction a Root, Request, and Transaction entities will have an ID of 1
        //       (then we will denormalize the Root entity with ID = 1)

        const state = sc.getState();

        let origTran = state.entities.Transaction[0];
        let origReply = state.entities.Reply[0];

        let processRoot = {
            '?xml': { '@version': '1.0' },
            ConceptOneRequest: '1'
        };

        let tranType = state.transactionType + 'Process';
        let tranNode = state.transactionType;
        let processTran = {};

        if (state.transactionType == TransactionNodeType.Contract) {
            //ContractProcess
            processTran = {
                View: 'Full',
                Book_Entity_ID: origTran.Book_Entity_ID,
                Book_Loc_ID: origTran.Book_Loc_ID,
                Contract: origTran.Contract,
                ...action.tranOptions
            };
        } else {
            //ApplicationProcess, BindProcess, etc.
            processTran = {
                View: 'Full',
                Book_Entity_ID: origTran.Book_Entity_ID,
                Book_Loc_ID: origTran.Book_Loc_ID,
                [tranNode]: origTran[tranNode],
                ...action.tranOptions
            };
        }

        let udTableData = this.store.selectSnapshot(UserDefinedObjectState.userDefinedTableData);
        let current = this.store.selectSnapshot(TaskObjectState.currentInfo);
        this.helper.setTaskControlAttributes(processTran, current);

        let processRequest = {
            [tranType]: '1',
            SDK_Version: origReply.SDK_Version
        };

        sc.patchState({
            entities: {
                ...state.entities,
                Root: {
                    ...state.entities.Root,
                    [1]: processRoot
                },
                Request: {
                    ...state.entities.Request,
                    [1]: processRequest
                },
                Transaction: {
                    ...state.entities.Transaction,
                    [1]: processTran
                },
                UDTable: udTableData
            }
        });
    }

    @Action(SetContractObjectState)
    setContractObjectState(sc: StateContext<ContractObjectStateModel>, action: SetContractObjectState) {
        // Note: this is temporary to manually load state.  Later we'll have an action to load data and normalize.  
        // This does  NOT clone the passed in object.
        // A true "SetFullState" should deep clone the object. How will this trigger change for selectors at lower levels of state. Object destroy?

        // TODO?  Is it proper to set substate directly here?   OR dow we need Set_State in each substate so we have an initialize call?

        const state = sc.getState();

        let tranType = action.entities.Transaction[0].nodeType;

        //Find the first (and ONLY) contract in Contract entity object
        let contractID = null;
        let mainLineCode = null;
        for (var i in action.entities.Contract) {
            contractID = i;
            mainLineCode = action.entities.Contract[i].Line_Code;
            break;
        }

        let procID = null;
        for (var i in action.entities.Procedure) {
            procID = i;
            break;
        }

        let consumerID = null;
        let cnEntityName = '';
        for (var i in action.entities.Entity) {
            if (action.entities.Entity[i].nodeType == EntityNodeType.Consumer) {
                consumerID = i;
                if (action.entities.Entity[i].Entity_Type == 'I') {
                    if (action.entities.Entity[i].Indv_Name) {
                        if (action.entities.Entity[i].Indv_Name.Indv_First_Name) {
                            cnEntityName = action.entities.Entity[i].Indv_Name.Indv_First_Name;
                        }
                        if (action.entities.Entity[i].Indv_Name.Indv_Middle_Name) {
                            cnEntityName += ' ' + action.entities.Entity[i].Indv_Name.Indv_Middle_Name;
                        }
                        if (action.entities.Entity[i].Indv_Name.Indv_Last_Name) {
                            cnEntityName += ' ' + action.entities.Entity[i].Indv_Name.Indv_Last_Name;
                        }

                        action.entities.Entity[i].Entity_Name = cnEntityName;
                    }
                }
                break;
            }
        }

        let interestEntityName = '';
        for (var i in action.entities.Entity) {
            if (action.entities.Entity[i].nodeType == EntityNodeType.Third_Party) {
                if (action.entities.Entity[i].Indv_Name) {
                    if (action.entities.Entity[i].Indv_Name.Indv_First_Name) {
                        interestEntityName = action.entities.Entity[i].Indv_Name.Indv_First_Name;
                    }
                    if (action.entities.Entity[i].Indv_Name.Indv_Middle_Name) {
                        interestEntityName += ' ' + action.entities.Entity[i].Indv_Name.Indv_Middle_Name;
                    }
                    if (action.entities.Entity[i].Indv_Name.Indv_Last_Name) {
                        interestEntityName += ' ' + action.entities.Entity[i].Indv_Name.Indv_Last_Name;
                    }

                    action.entities.Entity[i].Entity_Name = interestEntityName;
                    action.entities.Entity[i].Entity_Type = 'I';
                } else {
                    action.entities.Entity[i].Entity_Type = 'O';
                }
            }
        }

        let primaryLine: any = {};
        for (var i in action.entities.Line) {
            if (action.entities.Line[i].Line_Code == mainLineCode) {
                primaryLine = {
                    Line_ID: action.entities.Line[i].Line_ID,
                    Line_Code: action.entities.Line[i].Line_Code,
                    Line_Code_Desc: action.entities.Line[i].Line_Code_Desc
                };
                break;
            }
        }

        //TODO: Hack to pass Principal Broker's Affiliate into Contract.Broker_Affiliate
        //Set default value first, then replace with Prin Broker's Affiliate if found.
        action.entities.Contract[contractID].Broker_Affiliate = { Entity_ID: '', Entity_Name: '' };

        for (var i in action.entities.Party) {
            if (action.entities.Party[i].Broker && action.entities.Party[i].Principal_Ind == 'Y') {
                if (action.entities.Party[i].Affiliate) {
                    action.entities.Contract[contractID].Broker_Affiliate = { ...action.entities.Party[i].Affiliate };
                    break;
                }
            }
            if (action.entities.Party[i].Broker || action.entities.Party[i].Supplier) {
                action.entities.Party[i].agreementValidated = 'Y';
            }
        }

        for (var i in action.entities.Form) {
            if (action.entities.Form[i].Edit_Status != '?') {
                action.entities.Form[i].Selected_Ind = 'Y';
            } else {
                action.entities.Form[i].Selected_Ind = 'N';
            }
        }

        for (var i in action.entities.Condition) {
            if (action.entities.Condition[i].Edit_Status != '?') {
                action.entities.Condition[i].Selected_Ind = 'Y';
            } else {
                action.entities.Condition[i].Selected_Ind = 'N';
            }
        }

        let tpBillToEntity: any = {};
        if (action.entities.Contract[contractID].Bill_To_Role_Type == 'TP') {
            tpBillToEntity = action.entities.Contract[contractID].Bill_To_Entity;
        }

        //Set Edit_Status = "X" for records with a Deleted Status
        for (var i in action.entities.Article) {
            if (action.entities.Article[i].Article_Status == 'D') {
                action.entities.Article[i].Edit_Status = 'X';
            }
        }
        for (var i in action.entities.Form) {
            if (action.entities.Form[i].Article_Status == 'D') {
                action.entities.Form[i].Edit_Status = 'X';
                action.entities.Form[i].Selected_Ind = 'N';
            }
        }
        for (var i in action.entities.Attachment) {
            if (action.entities.Attachment[i].Status == 'D') {
                action.entities.Attachment[i].Edit_Status = 'X';
            }
        }
        for (var i in action.entities.Location) {
            if (action.entities.Location[i].Status == 'D') {
                action.entities.Location[i].Edit_Status = 'X';
            }
        }
        for (var i in action.entities.SubLocation) {
            if (action.entities.SubLocation[i].Status == 'D') {
                action.entities.SubLocation[i].Edit_Status = 'X';
            }
        }
        for (var i in action.entities.Interest) {
            if (action.entities.Interest[i].Status == 'D') {
                action.entities.Interest[i].Edit_Status = 'X';
            }
        }
        for (var i in action.entities.Name) {
            if (action.entities.Name[i].Status == 'D') {
                action.entities.Name[i].Edit_Status = 'X';
            }
        }
        for (var i in action.entities.Profile) {
            if (action.entities.Profile[i].Status == 'D') {
                action.entities.Profile[i].Edit_Status = 'X';
            }
        }
        for (var i in action.entities.Product) {
            if (action.entities.Product[i].Cost_Status == 'D' || action.entities.Product[i].Cost_Status == 'R') {
                action.entities.Product[i].Edit_Status = 'X';
            }
        }

        // copy references to raw normalized entities and then remove the entities that have their own state
        let entities = { ...action.entities };
        delete entities.Contract;
        delete entities.Procedure;
        delete entities.Entity;
        delete entities.Address;
        delete entities.Phone;
        delete entities.RiskGroup;
        delete entities.Line;
        delete entities.Party;
        delete entities.Article;
        delete entities.Form;
        delete entities.Factor;
        delete entities.Product;
        delete entities.Location;
        delete entities.SubLocation;
        delete entities.Interest;
        delete entities.Name;
        delete entities.Condition;
        delete entities.Profile;
        delete entities.Attachment;
        delete entities.TeamMember;
        delete entities.PriorHistory;
        delete entities.UDTable;

        // Make sure that entities "selector" get entities from all substates.   if a new substate is added here then we need to add to the selector
        sc.patchState({
            entities: entities,
            transactionType: tranType,
            contractProcedureState: {
                ...state.contractProcedureState,
                procedure: action.entities.Procedure || {},
                procedureType: tranType,
                currentID: procID
            },
            contractState: {
                ...state.contractState,
                contract: action.entities.Contract || {},
                currentID: contractID
            },
            entityState: {
                ...state.entityState,
                entity: action.entities.Entity || {},
                currentConsumerID: consumerID
            },
            addressState: { ...state.addressState, address: action.entities.Address || {} },
            phoneState: { ...state.phoneState, phone: action.entities.Phone || {} },
            riskGroupState: { ...state.riskGroupState, riskGroup: action.entities.RiskGroup || {} },

            lineState: {
                ...state.lineState,
                line: action.entities.Line || {},
                primaryLine: primaryLine || {}
            },
            partyState: { ...state.partyState, party: action.entities.Party || {}, tpBillToEntity: tpBillToEntity || {} },
            articleState: {
                ...state.articleState,
                article: action.entities.Article || {},
                subArticle: action.entities.SubArticle || {}
            },
            formState: { ...state.formState, form: action.entities.Form || {} },
            factorState: { ...state.factorState, factor: action.entities.Factor || {} },
            productState: { ...state.productState, product: action.entities.Product || {} },
            locationState: { ...state.locationState, location: action.entities.Location || {} },
            subLocationState: { ...state.subLocationState, subLocation: action.entities.SubLocation || {} },
            interestState: { ...state.interestState, interest: action.entities.Interest || {} },
            nameState: { ...state.nameState, name: action.entities.Name || {} },
            conditionState: { ...state.conditionState, condition: action.entities.Condition || {} },
            profileState: { ...state.profileState, profile: action.entities.Profile || {} },
            contractAttachmentState: { ...state.contractAttachmentState, attachment: action.entities.Attachment || {} },
            teamMemberState: { ...state.teamMemberState, teamMember: action.entities.TeamMember || {} },
            priorHistoryState: {
                ...state.priorHistoryState,
                priorHistory: action.entities.PriorHistory || {},
                lossHistory: action.entities.LossHistory || {}
            }
        });

        //Load User Defined Table Data into User Defined Table State
        this.store.dispatch(new SetUserDefinedTableData(action.entities.UDTable /*consumerID, contractID */));
    }

    @Action(ResetState)
    resetState(sc: StateContext<ContractObjectStateModel>, action: ResetState) {
        sc.patchState({
            entities: {},
            transactionType: null,
            contractProcedureState: { procedure: {}, procedureType: null, currentID: null },
            contractState: { contract: {}, currentID: null },
            entityState: { entity: {}, currentConsumerID: null },
            addressState: { address: {} },
            phoneState: { phone: {} },
            riskGroupState: { riskGroup: {} },
            lineState: { line: {}, primaryLine: {} },
            partyState: { party: {}, tpBillToEntity: {} },
            articleState: { article: {}, subArticle: {} },
            formState: { form: {} },
            factorState: { factor: {} },
            productState: { product: {} },
            locationState: { location: {} },
            subLocationState: { subLocation: {} },
            interestState: { interest: {} },
            nameState: { name: {} },
            conditionState: { condition: {} },
            profileState: { profile: {} },
            contractAttachmentState: { attachment: {} },
            teamMemberState: { teamMember: {} },
            priorHistoryState: { priorHistory: {}, lossHistory: {} }
        });

        //reset userdefined state
        this.store.dispatch(new UserDefinedResetState());
    }

    @Action(InitializeTransaction)
    initializeTransaction(sc: StateContext<ContractObjectStateModel>, action: InitializeTransaction) {
        let tran = action.transactionType + 'Get';
        let procedureState: any = {};

        // if a contract proc (ie. not contract edit) then build procedure node
        if (action.transactionType != TransactionNodeType.Contract) {
            procedureState.procedureType = action.transactionType;
            procedureState.currentID = -1;
            procedureState.procedure = {
                '-1': {
                    Proc_ID: '-1',
                    Contract: '-1'
                }
            };
        }

        sc.patchState({
            entities: {
                Root: { 0: { ConceptOneReply: '0' } },
                Reply: { 0: { [tran]: '0' } },
                Transaction: {
                    0: {
                        Book_Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID,
                        Book_Loc_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID,
                        [action.transactionType]: '-1'
                    },
                    nodeType: action.transactionType
                }
            },
            transactionType: action.transactionType,
            contractProcedureState: procedureState,
            contractState: { contract: {}, currentID: '-1' }
        });
    }

    @Action(AddDefaultLocation)
    addDefaultLocation(sc: StateContext<ContractObjectStateModel>, action: AddDefaultLocation) {
        const currentLocations = this.store.selectSnapshot(LocationState.locations);
        const consumerAddressIDs = this.store.selectSnapshot(EntityState.consumer)?.Addresses?.Address;
        const dfltAddress = this.store
            .selectSnapshot(AddressState.addressesByIDs)(consumerAddressIDs)
            ?.filter((address) => address.Dflt_Address_Ind == 'Y' && address.Edit_Status != 'D')[0];

        //Adds or Updates location based on CN default address.
        if (dfltAddress) {
            switch (currentLocations.length) {
                case 0:
                    sc.dispatch([
                        new AddLocation({
                            Loc_Num: '1',
                            Address_Line_1: dfltAddress.Address_Line_1 || '',
                            Address_Line_2: dfltAddress.Address_Line_2 || '',
                            City: dfltAddress.City || '',
                            State: dfltAddress.State || '',
                            Zip: dfltAddress.Zip || '',
                            County: dfltAddress.County || '',
                            Country: dfltAddress.Country || ''
                        })
                    ]);
                    break;

                case 1:
                    let location = cloneDeep(currentLocations[0]);

                    if (!location.Address_Line_1 && !location.Address_Line_2 && !location.City && !location.Zip) {
                        location.Address_Line_1 = dfltAddress.Address_Line_1 || '';
                        location.Address_Line_2 = dfltAddress.Address_Line_2 || '';
                        location.City = dfltAddress.City || '';
                        location.State = dfltAddress.State || '';
                        location.Zip = dfltAddress.Zip || '';
                        location.County = dfltAddress.County || '';
                        location.Country = dfltAddress.Country || '';

                        sc.dispatch([new UpdateLocation(location)]);
                    }
                    break;
            }
        }
    }

    @Action(SyncBrokerPartyFromContractBroker)
    syncBrokerPartyFromContractBroker(
        sc: StateContext<ContractObjectStateModel>,
        action: SyncBrokerPartyFromContractBroker
    ) {
        const state = sc.getState();
        const currentparties = state.partyState.party;
        let prevBroker = action.prevParty;
        let newBroker = action.newParty;
        let consumer = state.entityState.entity[state.entityState.currentConsumerID];
        let contract = state.contractState.contract[state.contractState.currentID];
        let addBroker = true;

        if (newBroker.Entity_ID == '') {
            //Broker Record Cleared
            //Find all party records with old broker name and Delete them
            for (var i in currentparties) {
                if (
                    currentparties[i].nodeType &&
                    currentparties[i].nodeType == PartyNodeType.Contract_Broker &&
                    currentparties[i].Broker &&
                    currentparties[i].Broker.Entity_Name == prevBroker.Entity_Name &&
                    currentparties[i].Edit_Status != 'D'
                ) {
                    sc.dispatch([new DeleteParty(currentparties[i].Party_ID)]);
                }
            }
            //Check if current Bill_To was the prevBroker. If so, change to CN when clearing Broker.
            if (
                contract.Bill_To_Role_Type == 'BK' &&
                contract.Bill_To_Entity &&
                contract.Bill_To_Entity.Entity_Name == prevBroker.Entity_Name
            ) {
                sc.dispatch([new SetBillToEntity(consumer, 'CN')]);
            }
        } else {
            // New Broker record selected.
            // Update non-deleted Broker records that have old Broker to use new Broker.
            for (var i in currentparties) {
                if (
                    currentparties[i].nodeType &&
                    currentparties[i].nodeType == PartyNodeType.Contract_Broker &&
                    currentparties[i].Broker &&
                    currentparties[i].Broker.Entity_Name == prevBroker.Entity_Name &&
                    currentparties[i].Edit_Status != 'D'
                ) {
                    addBroker = false;
                    let updateBrokerParty = this.helper.createDefaultItem(ContractObjectDefaults, 'Contract_Broker', {});
                    updateBrokerParty.Party_ID = currentparties[i].Party_ID;
                    updateBrokerParty.Broker.Entity_ID = newBroker.Entity_ID;
                    updateBrokerParty.Broker.Entity_Name = newBroker.Entity_Name;
                    sc.dispatch([new ClearParty(updateBrokerParty.Party_ID)]);

                    sc.dispatch([new UpdateParty(updateBrokerParty)]).subscribe(() => {
                        this.autoSelectAgmtServce.initializeService();
                        this.autoSelectAgmtServce.autoAddBrokerAgreement(updateBrokerParty);
                    });
                }
            }

            //if contract receivable role is BK, then set bill to field to broker entity name
            //only applies to new consumers on an application with default consumer to broker owned book option
            if (contract.Receivable_Role == 'BK') {
                sc.dispatch([new SetBillToEntity(newBroker, 'BK')]);
            }

            //Always dispatch this action
            sc.dispatch([
                new SetRetailBroker(contract.Consumer, newBroker) //This action only sets the Retail Broker if currently null.
            ]);

            //Only dispatch this action if no existing records were updated.
            if (addBroker) {
                let brokerParty = {
                    Broker: {
                        Entity_ID: newBroker.Entity_ID,
                        Entity_Name: newBroker.Entity_Name
                    },
                    Principal_Ind: 'Y',
                    Participation_Pct: '100.0000',
                    Net_Broker_Ind: 'Y'
                };

                sc.dispatch([new AddParty(PartyNodeType.Contract_Broker, brokerParty)]).subscribe(() => {
                    this.autoSelectAgmtServce.initializeService();
                    this.autoSelectAgmtServce.autoAddBrokerAgreement(brokerParty);
                });
            }
        }
    }

    @Action(SyncBrokerPartyAffiliateFromContractBrokerAffiliate)
    syncBrokerPartyAffiliateFromContractBrokerAffiliate(
        sc: StateContext<ContractObjectStateModel>,
        action: SyncBrokerPartyAffiliateFromContractBrokerAffiliate
    ) {
        const currentparties = sc.getState().partyState.party;
        const broker = action.broker;
        const affiliate = action.newAffiliate;
        let brokerParty = null;

        for (var i in currentparties) {
            if (
                currentparties[i].nodeType &&
                currentparties[i].nodeType == PartyNodeType.Contract_Broker &&
                currentparties[i].Broker &&
                currentparties[i].Broker.Entity_ID == broker.Entity_ID
            ) {
                brokerParty = cloneDeep(currentparties[i]);
                brokerParty.Affiliate = affiliate;

                sc.dispatch([new UpdateParty(brokerParty)]);
            }
        }
    }

    @Action(LinkProfileToArticle)
    linkProfileToArticle(sc: StateContext<ContractObjectStateModel>, action: LinkProfileToArticle) {
        let updateArticle = { ...sc.getState().articleState.article[action.articleID] };
        updateArticle.Profile_ID = action.profileID;
        sc.dispatch([new UpdateArticle(updateArticle)]);
    }

    @Action(DeleteArticlesLinkedToProfile)
    deleteArticlesLinkedToProfile(sc: StateContext<ContractObjectStateModel>, action: DeleteArticlesLinkedToProfile) {
        let profileID = action.profileID;
        let articles = { ...sc.getState().articleState.article };
        for (var i in articles) {
            if (articles[i].Profile_ID && articles[i].Profile_ID == profileID) {
                sc.dispatch([new DeleteArticle(articles[i].Article_ID)]);
            }
        }
    }

    @Action(AddNewProfileForClonedArticle)
    addNewProfileForClonedArticle(sc: StateContext<ContractObjectStateModel>, action: AddNewProfileForClonedArticle) {
        let updateArticle = action.article;
        let profile = cloneDeep(sc.getState().profileState.profile[updateArticle.Profile_ID]);
        if (profile) {
            delete profile.Profile_ID;
            delete profile.Edit_Status;
        }
        sc.dispatch([new AddProfile(profile, updateArticle.Article_ID)]);
    }

    @Action(SyncProcedureEstFullTermPrem)
    syncProcedureEstFullTermPrem(sc: StateContext<ContractObjectStateModel>, action: SyncProcedureEstFullTermPrem) {
        const state = sc.getState().contractProcedureState;
        const procID = state.currentID;
        let updateProc = { ...state.procedure[procID] };
        if (updateProc && updateProc.Proc_ID) {
            updateProc.Est_Full_Term_Prem = action.amount;

            sc.dispatch(new UpdateContractProcedure(updateProc));
        }
    }

    @Action(SelfAuditCalculateReporting)
    selfAuditCalculateReporting(sc: StateContext<ContractObjectStateModel>, action: SelfAuditCalculateReporting) {
        let products = cloneDeep(sc.getState().productState.product);
        let calcType = action.calcType;
        let calcPct = Number(action.calcPct);
        let round = action.round;
        let reportedAmt: number = 0.0;
        let fullTermAmt: number = 0.0;

        for (var i in products) {
            reportedAmt = 0.0;
            products[i].Net_Change_Amount = '0.00';

            fullTermAmt = Number(products[i].Full_Term_Amount);
            switch (calcType) {
                case 'D':
                    if (products[i].Product_Cost_Type != 'T') {
                        if (products[i].Reporting_Fully_Earned_Ind == 'Y') {
                            reportedAmt = fullTermAmt;
                        } else {
                            reportedAmt = Number((fullTermAmt * (calcPct / 100.0)).toFixed(2));
                        }
                    }
                    break;
                case 'S':
                    if (products[i].Product_Cost_Type != 'T') {
                        if (products[i].Reporting_Fully_Earned_Ind != 'Y') {
                            reportedAmt = Number((fullTermAmt * (calcPct / 100.0)).toFixed(2));
                        }
                    }
                    break;
            }

            if (reportedAmt > 0.0) {
                if (round) {
                    products[i].Net_Change_Amount = Math.round(reportedAmt).toString();
                } else {
                    products[i].Net_Change_Amount = reportedAmt.toString();
                }
            }
        }

        sc.patchState({
            productState: { product: products }
        });

        this.store.dispatch(new UpdateProductTotals());
    }
}
